import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useBookAppointment from "../../hooks/useBookAppointment";
import usePatientAvailability from "../../../appointments/hooks/usePatientAvailability";
import useSousProfiles from "../../../sous-profile/hooks/useSousProfiles";
import { fetchUsers } from "../../../Profile/redux/thunks/userThunks";
import { useDispatch } from "react-redux";
import "./BookAppointmentModal.css";

const DEFAULT_PATIENTS = [];

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

// ─── SUPPRIMÉ : STATIC_TIME_SLOTS n'est plus utilisé ─────────────────────────
// On garde uniquement pour fallback UI si aucune dispo API (message vide)

const normalizeSource = (source) =>
  String(source || "local").trim().toLowerCase();

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    if (Array.isArray(value.data)) return value.data;
    if (Array.isArray(value.items)) return value.items;
    if (Array.isArray(value.results)) return value.results;
    if (Array.isArray(value.patients)) return value.patients;
    if (Array.isArray(value.slots)) return value.slots;
  }
  return [];
};

const asLabelArray = (value, fallback = []) => {
  const entries = asArray(value);
  if (!entries.length) return fallback;
  const labels = entries
    .map((entry) => {
      if (typeof entry === "string") return entry.trim();
      if (entry && typeof entry === "object") {
        return String(
          entry.label ?? entry.name ?? entry.value ?? entry.title ?? ""
        ).trim();
      }
      return String(entry ?? "").trim();
    })
    .filter(Boolean);
  return labels.length ? labels : fallback;
};

const formatSourceLabel = (source) => {
  const normalized = normalizeSource(source);
  return normalized
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : "Local";
};

const formatTimeValue = (date) =>
  `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;

const formatPatientTimeLabel = (date) => {
  const h = date.getHours();
  const m = date.getMinutes();
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
};

const pad = (n) => String(n).padStart(2, "0");

// ─── FIX CRITIQUE : dateKey en LOCAL (pas UTC) pour éviter décalage fuseau ───
const dateKey = (d) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const formatDisplayTimeRange = (startTime, endTime) => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  return `${start.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })} - ${end.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

// ─── Format heure simple HH:MM pour un slot ──────────────────────────────────
const formatSlotTime = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
};

const normalizePatient = (patient, fallbackSource = "medilink") => {
  if (!patient) return null;
  const source = normalizeSource(patient.source || fallbackSource);
  const displayName =
    patient.displayName ||
    patient.fullName ||
    patient.name ||
    `${patient.firstName || ""} ${patient.lastName || ""}`.trim() ||
    `${patient.givenName || ""} ${patient.familyName || ""}`.trim() ||
    patient.username ||
    "Unknown";
  const phone =
    patient.phone || patient.mobile || patient.telephone || patient.tel || "";
  const email = patient.email || patient.mail || "";
  const id =
    patient.id ??
    patient._id ??
    `${source}-${displayName}-${phone || email || "na"}`;
  return {
    ...patient,
    id,
    name: displayName,
    displayName,
    fullName: displayName,
    phone,
    email,
    dateOfBirth:
      patient.dateOfBirth || patient.birthdate || patient.birthDate || null,
    source,
    searchBlob: [
      displayName,
      patient.firstName,
      patient.lastName,
      patient.fullName,
      patient.username,
      phone,
      email,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  };
};

const mergePatients = (...groups) => {
  const seen = new Set();
  const merged = [];
  for (const group of groups) {
    for (const patient of asArray(group)) {
      const normalized = normalizePatient(patient, patient?.source || "local");
      if (!normalized) continue;

      const normalizedName = String(normalized.displayName || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
      const normalizedPhone = String(normalized.phone || "").replace(/\D/g, "");
      const normalizedEmail = String(normalized.email || "").trim().toLowerCase();
      const normalizedBirthDate = String(normalized.dateOfBirth || "").slice(0, 10);
      const identityKeys = [
        `${normalized.source}:${String(normalized.id)}`,
        normalizedPhone && `phone:${normalizedPhone}`,
        normalizedEmail && `email:${normalizedEmail}`,
        normalizedName && normalizedBirthDate && `name-dob:${normalizedName}:${normalizedBirthDate}`,
      ].filter(Boolean);

      if (identityKeys.some((key) => seen.has(key))) continue;
      identityKeys.forEach((key) => seen.add(key));
      merged.push(normalized);
    }
  }
  return merged;
};

const parseIncomingDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const getTodayDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

const isSameLocalDay = (dateA, dateB) => {
  if (!dateA || !dateB) return false;
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// ─── Step 1 : Patient ─────────────────────────────────────────────────────────

function StepPatient({ patients = [], selected, onSelect, onContinue, tText }) {
  const [tab, setTab] = useState("existing");
  const [search, setSearch] = useState("");

  const [newForm, setNewForm] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "Male",
    phone: "",
    email: "",
  });

  const setField = (field) => (event) =>
    setNewForm((current) => ({ ...current, [field]: event.target.value }));

  const filteredPatients = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return patients;
    return patients.filter((patient) => {
      const haystack =
        patient.searchBlob ||
        `${patient.displayName || patient.name || ""} ${patient.phone || ""} ${
          patient.email || ""
        }`.toLowerCase();
      return (
        haystack.includes(query) || String(patient.phone || "").includes(search)
      );
    });
  }, [patients, search]);

  const calculateAge = (dateString) => {
    if (!dateString) return null;
    const birthDate = new Date(dateString);
    if (Number.isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }
    return age >= 0 ? age : null;
  };

  const calculatedAge = calculateAge(newForm.dateOfBirth);

  const canContinue =
    tab === "existing"
      ? !!selected
      : newForm.firstName.trim().length > 0 &&
        newForm.lastName.trim().length > 0 &&
        newForm.phone.trim().length > 0 &&
        newForm.dateOfBirth.trim().length > 0;

  function handleContinue() {
    if (tab === "new") {
      onSelect({
        id: `new-${Date.now()}`,
        name: `${newForm.firstName.trim()} ${newForm.lastName.trim()}`.trim(),
        age: calculatedAge || 30,
        dateOfBirth: newForm.dateOfBirth,
        source: "local",
        phone: newForm.phone,
        email: newForm.email,
        gender: newForm.gender,
        isNew: true,
      });
    }
    onContinue();
  }

  return (
    <div className="bam__step">
      <div className="bam__tabs">
        <button
          type="button"
          className={`bam__tab ${tab === "existing" ? "bam__tab--active" : ""}`}
          onClick={() => setTab("existing")}
        >
          <strong>{tText("bookModal.step1.tabExisting", "Existing Patients")}</strong>
          <span>{tText("bookModal.step1.tabExistingDesc", "Choose a patient from Medilink or local records")}</span>
        </button>
        <button
          type="button"
          className={`bam__tab ${tab === "new" ? "bam__tab--active" : ""}`}
          onClick={() => setTab("new")}
        >
          <strong>{tText("bookModal.step1.tabNew", "New Patient")}</strong>
          <span>{tText("bookModal.step1.tabNewDesc", "Create a local patient before booking")}</span>
        </button>
      </div>

      {tab === "existing" ? (
        <>
          <div className="bam__search-wrap">
            <SearchIcon />
            <input
              className="bam__search"
              placeholder={tText("bookModal.step1.searchPlaceholder", "Search patients...")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="bam__patient-list">
            {filteredPatients.map((patient) => (
              <div
                key={`${patient.source}:${patient.id}`}
                className={`bam__patient-row ${
                  selected?.id === patient.id ? "bam__patient-row--selected" : ""
                }`}
                onClick={() =>
                  onSelect({
                    ...patient,
                    id: patient.id,
                    name: patient.displayName,
                    age: patient.age || 30,
                    source: normalizeSource(patient.source),
                    phone: patient.phone || "",
                    email: patient.email || "",
                    gender: patient.gender || "",
                    isNew: false,
                  })
                }
              >
                <div className="bam__patient-info">
                  <span className="bam__patient-name">{patient.displayName}</span>
                  <span className="bam__patient-phone">
                    {patient.phone || patient.email || tText("bookModal.step1.noContact", "No contact info")}
                  </span>
                </div>
                <span className={`bam__source-badge bam__source-badge--${normalizeSource(patient.source)}`}>
                  {formatSourceLabel(patient.source)}
                </span>
              </div>
            ))}
            {filteredPatients.length === 0 && (
              <p className="bam__empty-list">
                {search
                  ? tText("bookModal.step1.noSearchMatch", `No patient matching "${search}"`, { search })
                  : tText("bookModal.step1.noPatients", "No patients found")}
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="bam__new-form">
          <div className="bam__form-row">
            <div className="bam__form-col">
              <label className="bam__label">{tText("bookModal.step1.firstName", "First name")}</label>
              <input className="bam__input" placeholder={tText("bookModal.step1.firstNamePlaceholder", "e.g., Amina")} value={newForm.firstName} onChange={setField("firstName")} />
            </div>
            <div className="bam__form-col">
              <label className="bam__label">{tText("bookModal.step1.lastName", "Last name")}</label>
              <input className="bam__input" placeholder={tText("bookModal.step1.lastNamePlaceholder", "e.g., Ben Ali")} value={newForm.lastName} onChange={setField("lastName")} />
            </div>
          </div>

          <div className="bam__form-row">
            <div className="bam__form-col">
              <label className="bam__label">{tText("bookModal.step1.dateOfBirth", "Date of birth")}</label>
              <input className="bam__input" type="date" value={newForm.dateOfBirth} onChange={setField("dateOfBirth")} />
            </div>
            <div className="bam__form-col">
              <label className="bam__label">{tText("bookModal.step1.age", "Age")}</label>
              <input className="bam__input" type="number" value={calculatedAge ?? ""} readOnly placeholder={tText("bookModal.step1.agePlaceholder", "Calculated automatically")} />
            </div>
            <div className="bam__form-col">
              <label className="bam__label">{tText("bookModal.step1.gender", "Gender")}</label>
              <div className="bam__gender-row">
                {[
                  { key: "Male", label: tText("bookModal.step1.male", "Male") },
                  { key: "Female", label: tText("bookModal.step1.female", "Female") },
                ].map(({ key, label }) => (
                  <label key={key} className="bam__gender-option">
                    <span
                      className={`bam__gender-dot ${newForm.gender === key ? "bam__gender-dot--active" : ""}`}
                      onClick={() => setNewForm((current) => ({ ...current, gender: key }))}
                    />
                    <span
                      className="bam__gender-label"
                      onClick={() => setNewForm((current) => ({ ...current, gender: key }))}
                    >
                      {label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <label className="bam__label">{tText("bookModal.step1.phone", "Phone")}</label>
          <input className="bam__input" placeholder={tText("bookModal.step1.phonePlaceholder", "+216 XX XXX XXX")} value={newForm.phone} onChange={setField("phone")} />

          <label className="bam__label">
            {tText("bookModal.step1.email", "Email")}{" "}
            <span className="bam__label-optional">{tText("bookModal.step1.optional", "optional")}</span>
          </label>
          <input className="bam__input" type="email" placeholder={tText("bookModal.step1.emailPlaceholder", "name@example.tn")} value={newForm.email} onChange={setField("email")} />
        </div>
      )}

      <button
        type="button"
        className={`bam__btn-primary bam__btn-full ${!canContinue ? "bam__btn--disabled" : ""}`}
        onClick={handleContinue}
        disabled={!canContinue}
      >
        {tText("bookModal.step1.continue", "Continue")}
      </button>
    </div>
  );
}

// ─── Step 2 : Disponibilité réelle du médecin ─────────────────────────────────
// FIX : Utilise uniquement les vrais créneaux API. Pas de fakedata.

function StepDateTime({
  medecinId,
  slotsByKey,
  loadingSlots,
  selectedSlot,
  onDateChange,
  onSlotChange,
  onBack,
  onContinue,
  tText,
}) {
  const today = getTodayDate();

  // Force la date = aujourd'hui dès le montage
  useEffect(() => {
    onDateChange(today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── FIX CRITIQUE : dateKey LOCAL (pas UTC) ───────────────────────────────
  const todayKey = dateKey(today); // ex: "2026-05-29"
  const slotsKey = medecinId && todayKey ? `${medecinId}|${todayKey}` : "";

  // Debug log — à supprimer en production
  useEffect(() => {
  }, [slotsKey, slotsByKey]);

  // ─── Vrais créneaux uniquement — PAS de fallback fakedata ─────────────────
  const realSlots = Array.isArray(slotsByKey?.[slotsKey]) ? slotsByKey[slotsKey] : [];

  const canContinue = !!selectedSlot;

  const todayLabel = today.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="bam__step">
      {/* Carte date du jour */}
      <div className="bam__today-card">
        <div className="bam__today-icon">
          <CalendarIcon />
        </div>
        <div className="bam__today-content">
          <strong style={{ textTransform: "capitalize" }}>{todayLabel}</strong>
          <span>{tText("bookModal.step2.todayAvailability", "Today's availability")}</span>
        </div>
      </div>

      {/* Titre créneaux */}
      <p className="bam__section-label bam__section-label--slots">
        <span className="bam__slots-label-icon">
          <ClockIcon />
        </span>
        {tText("bookModal.step2.timeSlots", "Available time slots")}
      </p>

      {/* Liste des créneaux — uniquement depuis l'API */}
      {loadingSlots ? (
        <p className="bam__empty-list">
          {tText("bookModal.step2.loadingSlots", "Loading slots...")}
        </p>
      ) : realSlots.length === 0 ? (
        <p className="bam__empty-list">
          {tText("bookModal.step2.noAvailability", "No availability for today")}
        </p>
      ) : (
        <div className="bam__time-grid">
          {realSlots.map((slot) => {
            const isSelected = selectedSlot?.id === slot.id;

            // Affiche HH:MM - HH:MM pour les vrais créneaux
            const label = formatDisplayTimeRange(slot.startTime, slot.endTime);

            return (
              <button
                key={slot.id}
                type="button"
                className={`bam__time-slot ${isSelected ? "bam__time-slot--selected" : ""}`}
                onClick={() => onSlotChange(slot)}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div className="bam__btn-row">
        <button className="bam__btn-secondary" type="button" onClick={onBack}>
          {tText("bookModal.step2.back", "Back")}
        </button>
        <button
          type="button"
          className={`bam__btn-primary ${!canContinue ? "bam__btn--disabled" : ""}`}
          onClick={onContinue}
          disabled={!canContinue}
        >
          {tText("bookModal.step2.continue", "Continue")}
        </button>
      </div>
    </div>
  );
}

// ─── Step 3 : Confirmation ────────────────────────────────────────────────────

function StepConfirm({
  patient,
  selectedDate,
  selectedSlot,
  reason,
  onReasonChange,
  onBack,
  onConfirm,
  loading,
  tText,
}) {
  const dateStr = selectedDate
    ? `${String(selectedDate.getDate()).padStart(2, "0")}/${String(
        selectedDate.getMonth() + 1
      ).padStart(2, "0")}/${selectedDate.getFullYear()}`
    : "—";

  const timeStr = selectedSlot
    ? formatDisplayTimeRange(selectedSlot.startTime, selectedSlot.endTime)
    : "—";

  return (
    <div className="bam__step">
      <p className="bam__section-label">
        {tText("bookModal.step3.reasonLabel", "Reason")}
      </p>

      <textarea
        className="bam__textarea"
        placeholder={tText("bookModal.step3.reasonPlaceholder", "Describe the reason for this appointment")}
        value={reason}
        onChange={(event) => onReasonChange(event.target.value)}
        rows={3}
        disabled={loading}
      />

      <div className="bam__summary">
        <p className="bam__summary-title">
          {tText("bookModal.step3.summaryTitle", "Appointment Summary")}
        </p>
        <p className="bam__summary-row">
          <span>{tText("bookModal.step3.patient", "Patient")}:</span> {patient?.name || "—"}
        </p>
        <p className="bam__summary-row">
          <span>{tText("bookModal.step3.date", "Date")}:</span> {dateStr}
        </p>
        <p className="bam__summary-row">
          <span>{tText("bookModal.step3.time", "Time")}:</span> {timeStr}
        </p>
        {reason && (
          <p className="bam__summary-row">
            <span>{tText("bookModal.step3.reason", "Reason")}:</span> {reason}
          </p>
        )}
      </div>

      <div className="bam__btn-row">
        <button className="bam__btn-secondary" type="button" onClick={onBack} disabled={loading}>
          {tText("bookModal.step3.back", "Back")}
        </button>
        <button
          type="button"
          className={`bam__btn-primary bam__btn-confirm ${loading ? "bam__btn--disabled" : ""}`}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? (
            tText("bookModal.step3.processing", "Processing...")
          ) : (
            <>
              <CheckCircleIcon />
              {tText("bookModal.step3.confirm", "Confirm Booking")}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Modal principal ──────────────────────────────────────────────────────────

export default function BookAppointmentModal({
  medecinId,
  appointmentType = "new",
  initialPatient = null,
  prefilledDate = null,
  prefilledSlot = null,
  allPatients = [],
  localPatients = [],
  onClose,
  onBooked,
}) {
  const { t } = useTranslation("waitingRoom");
  const dispatch = useDispatch();

  const tText = (key, fallback, options) => {
    const translated = t(key, options);
    return translated === key ? fallback : translated;
  };

  const {
    localPatients: storedLocalPatients,
    localPatientsError,
    bookingLoading,
    bookingError,
    bookAppointment,
  } = useBookAppointment(medecinId);

  const isAssistantSession =
    typeof window !== "undefined" &&
    window.localStorage.getItem("userRole") === "assistant";
  const { items: subprofileItems } = useSousProfiles({
    autoFetch: !isAssistantSession,
  });

  const medilinkPatients = useSelector((state) => {
    const byId = state.user?.usersById || {};
    const users = state.user?.users || {};
    return [...Object.values(byId), ...Object.values(users)];
  });

  const medilinkHasFetched = useSelector((state) => state.user?.hasFetched);

  useEffect(() => {
    if (!medilinkHasFetched) {
      dispatch(fetchUsers());
    }
  }, [dispatch, medilinkHasFetched]);

  const { slotsByKey, loadingSlots, loadSlots } = usePatientAvailability();

  const normalizedInitialPatient = useMemo(
    () => normalizePatient(initialPatient, initialPatient?.source || "medilink"),
    [initialPatient]
  );
  const [step, setStep] = useState(normalizedInitialPatient ? 2 : 1);
  const [patient, setPatient] = useState(normalizedInitialPatient);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [selectedSlot, setSelectedSlot] = useState(prefilledSlot);
  const [reason, setReason] = useState("");

  const mergedPatients = useMemo(() => {
    const medilink = (allPatients || []).map((p) => ({ ...p, source: p.source || "medilink" }));
    const local = (localPatients || []).map((p) => ({ ...p, source: p.source || "local" }));
    const stored = (storedLocalPatients || []).map((p) => ({ ...p, source: p.source || "local" }));
    const subprofiles = (subprofileItems || []).map((p) => ({ ...p, source: p.source || "subprofile" }));

    const merged = mergePatients(medilink, medilinkPatients, local, stored, subprofiles);

    if (merged.length > 0) return merged;

    return DEFAULT_PATIENTS.map((item) => normalizePatient(item, item.source)).filter(Boolean);
  }, [allPatients, localPatients, storedLocalPatients, subprofileItems, medilinkPatients]);

  // ─── FIX : Charge les slots dès que medecinId est disponible ─────────────
  // useCallback-stable loadSlots → pas de boucle infinie
  useEffect(() => {
    if (!medecinId) return;
    const today = getTodayDate();
    loadSlots(medecinId, today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medecinId]); // On ne dépend PAS de loadSlots (stable via useCallback dans le hook)

  // Recharge si selectedDate change (toujours aujourd'hui dans ce mode)
  useEffect(() => {
    if (!medecinId || !selectedDate) return;

    loadSlots(medecinId, selectedDate);

    const dateHasChanged =
      prefilledDate && selectedDate.getTime() !== prefilledDate.getTime();
    if (dateHasChanged || !prefilledSlot) {
      setSelectedSlot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medecinId, selectedDate]); // Pas de loadSlots dans les deps

  const loading = bookingLoading;
  const error = bookingError || localPatientsError;

  async function handleConfirm() {
    if (!patient || !selectedDate || !selectedSlot) return;

    try {
      const selectedTime = selectedSlot?.startTime
        ? formatTimeValue(new Date(selectedSlot.startTime))
        : null;

      const booked = await bookAppointment({
        patient,
        selectedDate,
        selectedTime,
        reason,
      });

      onBooked?.(booked, patient, selectedDate, selectedSlot, reason);
      onClose?.();
    } catch (err) {
      console.error(err?.message || "Failed to book appointment", err);
    }
  }

  const steps = [
    tText("bookModal.steps.0", "Patient"),
    tText("bookModal.steps.1", "Date & Time"),
    tText("bookModal.steps.2", "Confirm"),
  ];

  return (
    <div
      className="bam__backdrop"
      onClick={(event) => event.target === event.currentTarget && onClose?.()}
    >
      <div className="bam__modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="bam__modal-header">
          <div>
            <h2 className="bam__modal-title">
              {tText("bookModal.title", "Book New Appointment")}
            </h2>
            <p className="bam__modal-sub">
              {tText("bookModal.subtitle", "Schedule a new consultation")}
            </p>
          </div>
          <button
            type="button"
            className="bam__close"
            onClick={onClose}
            aria-label="Close"
            disabled={loading}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Progress bar */}
        <div className="bam__progress">
          {steps.map((label, index) => (
            <div
              key={label}
              className={`bam__progress-seg ${index + 1 <= step ? "bam__progress-seg--done" : ""}`}
            />
          ))}
        </div>

        {/* Error */}
        {error && <div className="bam__error-box">{error}</div>}

        {/* Steps */}
        <div className="bam__body">
          {step === 1 && (
            <StepPatient
              patients={mergedPatients}
              selected={patient}
              onSelect={setPatient}
              onContinue={() => appointmentType === "add" ? setStep(3) : setStep(2)}
              tText={tText}
            />
          )}

          {appointmentType !== "add" && step === 2 && (
            <StepDateTime
              medecinId={medecinId}
              slotsByKey={slotsByKey}
              loadingSlots={loadingSlots}
              selectedSlot={selectedSlot}
              onDateChange={setSelectedDate}
              onSlotChange={setSelectedSlot}
              onBack={() => setStep(1)}
              onContinue={() => setStep(3)}
              tText={tText}
            />
          )}

          {step === 3 && (
            <StepConfirm
              patient={patient}
              selectedDate={selectedDate}
              selectedSlot={selectedSlot}
              reason={reason}
              onReasonChange={setReason}
              onBack={() => appointmentType === "add" ? setStep(1) : setStep(2)}
              onConfirm={handleConfirm}
              loading={loading}
              tText={tText}
            />
          )}
        </div>
      </div>
    </div>
  );
}